import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { categoryService, vehicleCatalogService } from "../../services/apiService";
import { FaSpinner } from "react-icons/fa";
import { categoryPlaceholder, onImgErrorToPlaceholder } from "../../utils/placeholderImage";

/** Shown when API returns no roots so Step 2 still has a classification grid after tier pick. */
const CATALOG_FALLBACK_CATEGORIES = [
  { title: "Engine", slug: "engine", img: categoryPlaceholder("Engine", 100) },
  { title: "Brakes", slug: "brakes", img: categoryPlaceholder("Brakes", 100) },
  { title: "Filters", slug: "filters", img: categoryPlaceholder("Filters", 100) },
  { title: "Transmission", slug: "transmission", img: categoryPlaceholder("Transmission", 100) },
  { title: "Clutch", slug: "clutch", img: categoryPlaceholder("Clutch", 100) },
  { title: "Electrical", slug: "electric_components", img: categoryPlaceholder("Electrical", 100) },
  { title: "Suspension", slug: "suspension", img: categoryPlaceholder("Suspension", 100) },
  { title: "Body", slug: "body", img: categoryPlaceholder("Body", 100) },
  { title: "Oils & Fluids", slug: "oilsfluids", img: categoryPlaceholder("Oils", 100) },
  { title: "Cooling System", slug: "cooling_system", img: categoryPlaceholder("Cooling", 100) },
  { title: "Exhaust", slug: "exhaust", img: categoryPlaceholder("Exhaust", 100) },
  { title: "Steering", slug: "steering", img: categoryPlaceholder("Steering", 100) },
];

const ReplacementParts = ({
  spareType: propSpareType = null,
  vehicleMake: propMake = '',
  vehicleModel: propModel = '',
  vehicleYear: propYear = '',
  vehicleVariant: propVariant = '',
  vehicleFuelType: propFuelType = '',
  catalogModelId: propCatalogModelId = '',
  onCategoryClick = null,
  activeCategory = null,
  origin: propOrigin = null,
  darkCatalog = false
}) => {
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(location.search);
  const spareType = propSpareType || urlParams.get('spareType') || null;
  const origin = propOrigin || urlParams.get('origin') || null;

  // Props from parent take priority; fall back to URL params
  const vehicleMake = propMake || urlParams.get('vehicleMake') || '';
  const vehicleModel = propModel || urlParams.get('vehicleModel') || '';
  const vehicleYear = propYear || urlParams.get('vehicleYear') || '';
  const rawVariant = propVariant || urlParams.get('vehicleVariant') || '';
  const vehicleVariant = (() => {
    if (!rawVariant || rawVariant === 'All' || !rawVariant.includes('/')) return rawVariant;
    const trimPart = rawVariant.split('/')[0].trim();
    return trimPart.replace(/^\d+[\d.]*[Lcc]+\s*/i, '').trim() || trimPart;
  })();
  const vehicleFuelType = propFuelType || urlParams.get('vehicleFuelType') || '';
  const catalogModelId = propCatalogModelId || urlParams.get('catalogModelId') || '';

  // Fetch categories from backend — filtered by vehicle + condition when present
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const vehicleParams = {};
        if (vehicleMake) vehicleParams.vehicleMake = vehicleMake;
        if (vehicleModel) vehicleParams.vehicleModel = vehicleModel;
        if (vehicleYear) vehicleParams.vehicleYear = vehicleYear;
        if (vehicleVariant) vehicleParams.vehicleVariant = vehicleVariant;
        if (vehicleFuelType) vehicleParams.vehicleFuelType = vehicleFuelType;
        if (spareType) vehicleParams.condition = spareType;
        if (origin) vehicleParams.origin = origin;
        let mappedCategories = [];

        // Prefer vehicle-catalog categories (Vehicle Management portal source) when model is known.
        if (catalogModelId) {
          try {
            const modelCategories = await vehicleCatalogService.listCategoriesPublic(catalogModelId);
            const vcList = Array.isArray(modelCategories) ? modelCategories : [];
            const vcWithParts = await Promise.all(
              vcList.map(async (cat) => {
                const categoryId = cat?._id || cat?.id;
                if (!categoryId) return null;
                try {
                  const parts = await vehicleCatalogService.listPartsPublic(categoryId, {
                    modelId: catalogModelId
                  });
                  const hasParts = Array.isArray(parts) && parts.length > 0;
                  return hasParts ? cat : null;
                } catch {
                  return null;
                }
              })
            );

            mappedCategories = vcWithParts
              .filter(Boolean)
              .map((cat) => {
                const categoryName = String(cat?.name || "").trim();
                if (!categoryName) return null;
                const rawSlug = String(cat?.slug || "")
                  .trim()
                  .toLowerCase();
                const normalizedSlug =
                  rawSlug ||
                  categoryName
                    .toLowerCase()
                    .replace(/\s+/g, "_")
                    .replace(/[^a-z0-9_]+/g, "");
                return {
                  title: categoryName,
                  slug: normalizedSlug,
                  img:
                    cat?.icon ||
                    categoryPlaceholder(categoryName, 80)
                };
              })
              .filter(Boolean);
          } catch {
            mappedCategories = [];
          }
        }

        const result = await categoryService.getAllActiveCategoriesFlat(vehicleParams);

        let categoriesData = [];
        if (result && result.categories && Array.isArray(result.categories)) {
          categoriesData = result.categories;
        } else if (Array.isArray(result)) {
          categoriesData = result;
        } else if (result && result.data && Array.isArray(result.data.categories)) {
          categoriesData = result.data.categories;
        } else if (result && result.data && Array.isArray(result.data)) {
          categoriesData = result.data;
        }

        // Fallback to marketplace/public categories only when vehicle-catalog did not return model categories.
        if (!mappedCategories.length) {
          mappedCategories = categoriesData
            .map(cat => {
              let categoryName;
              if (typeof cat === 'string') {
                categoryName = cat;
              } else if (cat && cat.name) {
                categoryName = cat.name;
              } else {
                return null;
              }
              if (!categoryName || typeof categoryName !== 'string') return null;

              // Skip subcategories — only show root/parent categories in the grid
              const parentId = cat.parentCategory
                ? (cat.parentCategory._id || cat.parentCategory)
                : null;
              if (parentId) return null;

              // Skip parent categories that have children defined in DB but none
              // are available for the selected vehicle (subcategoryCount === 0)
              if (typeof cat.subcategoryCount === 'number' && cat.subcategoryCount === 0) {
                // Only hide if it actually has children (subcategoryCount being 0 with children
                // means backend already excluded it, but guard here too)
                return null;
              }

              return {
                title: categoryName,
                slug: cat.slug || categoryName.toLowerCase().replace(/\s+/g, '_'),
                img: cat.icon || categoryPlaceholder(categoryName, 80)
              };
            })
            .filter(cat => cat && cat.title);
        }

        const tierSelected = !!(spareType || origin);
        if (mappedCategories.length === 0 && tierSelected) {
          setCategories(CATALOG_FALLBACK_CATEGORIES);
        } else {
          setCategories(mappedCategories.length > 0 ? mappedCategories : []);
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        const tierSelected = !!(spareType || origin);
        setCategories(tierSelected ? CATALOG_FALLBACK_CATEGORIES : []);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, [vehicleMake, vehicleModel, vehicleYear, vehicleVariant, vehicleFuelType, spareType, origin, catalogModelId, propMake, propModel, propYear, propVariant, propFuelType, propCatalogModelId, propSpareType, propOrigin, location.search]);

  const filteredParts = categories.filter((part) =>
    part.title?.toLowerCase().includes(search.toLowerCase())
  );

  const handlePartClick = (part) => {
    if (onCategoryClick) {
      onCategoryClick(part);
      return;
    }
    const params = new URLSearchParams(location.search);
    navigate(`/catalog/${part.slug}?${params.toString()}`);
  };



  const sectionLabel = origin === 'Aftermarket'
    ? 'After Market Parts'
    : origin === 'OEM'
    ? 'OEM Parts'
    : spareType === 'used'
    ? 'Used Parts'
    : spareType === 'new'
    ? 'New Parts'
    : 'Replacement Parts';

  const accentColor = origin === 'Aftermarket'
    ? 'from-green-500 to-emerald-600'
    : origin === 'OEM'
    ? 'from-purple-500 to-purple-600'
    : spareType === 'used'
    ? 'from-amber-500 to-orange-500'
    : 'from-sky-600 to-blue-600';

  return (
    <section
      className={`max-w-7xl mx-auto px-2 sm:px-3 py-6 sm:py-8 md:py-10 ${
        darkCatalog ? "text-slate-200" : ""
      }`}
    >
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-8 sm:mb-10 gap-4">
        <div>
          <div className={`w-16 h-1 bg-gradient-to-r ${accentColor} mb-3 rounded-full`}></div>
          <h3
            className={`text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight ${
              darkCatalog ? "text-white" : "text-gray-900"
            }`}
          >
            {sectionLabel.split(" ")[0]}{" "}
            <span
              className={
                darkCatalog
                  ? spareType === "used"
                    ? "text-amber-400"
                    : "text-sky-400"
                  : spareType === "used"
                    ? "text-amber-500"
                    : "text-blue-600"
              }
            >
              {sectionLabel.split(" ").slice(1).join(" ")}
            </span>
          </h3>
          {(vehicleMake || vehicleModel) && (
            <p className={`mt-1 text-sm ${darkCatalog ? "text-slate-500" : "text-gray-500"}`}>
              Compatible with{" "}
              <span
                className={`font-semibold ${
                  darkCatalog ? "text-slate-200" : "text-gray-800"
                }`}
              >
                {vehicleMake} {vehicleModel}
                {vehicleYear ? ` · ${vehicleYear}` : ""}
                {vehicleVariant ? ` · ${vehicleVariant}` : ""}
              </span>
            </p>
          )}
        </div>

        <input
          type="search"
          placeholder="Filter Category"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={`w-full md:w-64 rounded-lg px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 ${
            darkCatalog
              ? "border border-slate-600 bg-slate-800/90 text-white placeholder:text-slate-500 focus:ring-amber-500/50"
              : "border border-gray-300 focus:ring-2 focus:ring-sky-500"
          }`}
        />
      </div>

      {/* Grid List */}
      {loadingCategories ? (
        <div
          className={`flex justify-center items-center py-20 rounded-3xl border shadow-sm ${
            darkCatalog
              ? "bg-slate-800/50 border-slate-700"
              : "bg-white border-gray-100"
          }`}
        >
          <div className="flex flex-col items-center gap-4">
            <div
              className={`w-12 h-12 border-4 border-t-transparent rounded-full animate-spin ${
                darkCatalog ? "border-amber-500" : "border-blue-600"
              }`}
            />
            <p
              className={`text-[10px] font-black uppercase tracking-widest ${
                darkCatalog ? "text-slate-500" : "text-gray-400"
              }`}
            >
              Loading Categories...
            </p>
          </div>
        </div>
      ) : filteredParts.length === 0 ? (
        <div
          className={`text-center py-20 rounded-[2.5rem] border-2 border-dashed ${
            darkCatalog
              ? "bg-slate-800/40 border-slate-600"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          {(vehicleMake || vehicleModel) && categories.length === 0 ? (
            <div className="max-w-md mx-auto">
              <div
                className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
                  darkCatalog ? "bg-slate-700" : "bg-gray-100"
                }`}
              >
                <span className="text-4xl grayscale">🚗</span>
              </div>
              <h4
                className={`text-xl font-black uppercase tracking-tighter mb-2 ${
                  darkCatalog ? "text-white" : "text-gray-900"
                }`}
              >
                No Matching Categories
              </h4>
              <p className={`text-sm font-medium ${darkCatalog ? "text-slate-400" : "text-gray-500"}`}>
                We currently don&apos;t have parts listed for your{" "}
                <span className={darkCatalog ? "text-amber-400 font-bold" : "text-gray-900 font-bold"}>
                  {vehicleMake} {vehicleModel}
                </span>
                .
              </p>
            </div>
          ) : (
            <div className="max-w-md mx-auto">
              <p
                className={`font-black uppercase tracking-widest text-xs ${
                  darkCatalog ? "text-slate-500" : "text-gray-400"
                }`}
              >
                No categories match your search.
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-4 md:gap-6">
          {filteredParts.map((part, index) => (
            <div
              key={index}
              onClick={() => handlePartClick(part)}
              className={`group relative flex aspect-square flex-col items-center justify-between p-2 sm:p-6 border rounded-xl sm:rounded-[2rem] transition-all duration-300 cursor-pointer overflow-hidden ${
                darkCatalog
                  ? activeCategory === part.slug
                    ? "border-amber-500/80 bg-slate-800/90 ring-1 ring-amber-500/40"
                    : "border-slate-700 bg-slate-800/60 hover:border-amber-500/40 hover:shadow-lg hover:shadow-black/30 sm:hover:-translate-y-1"
                  : activeCategory === part.slug
                    ? "border-blue-600 bg-blue-50/30 bg-white sm:hover:shadow-2xl sm:hover:shadow-blue-900/10 sm:hover:-translate-y-2"
                    : "border-gray-100 bg-white hover:border-blue-500/50 sm:hover:shadow-2xl sm:hover:shadow-blue-900/10 sm:hover:-translate-y-2"
              }`}
            >
              <div
                className={`absolute inset-0 bg-gradient-to-br transition-opacity duration-500 ${
                  darkCatalog
                    ? "from-amber-500/5 to-transparent opacity-0 group-hover:opacity-100"
                    : `from-blue-50/50 to-transparent ${activeCategory === part.slug ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`
                }`}
              />

              <div
                className={`relative z-10 flex flex-1 w-full min-h-0 items-center justify-center p-1 sm:p-2 rounded-lg sm:rounded-2xl transition-colors duration-300 ${
                  darkCatalog
                    ? "bg-slate-900/80 group-hover:bg-slate-900"
                    : "bg-gray-50/50 group-hover:bg-white"
                }`}
              >
                <img
                  loading="lazy"
                  src={part.img}
                  alt={part.title}
                  className="max-w-full max-h-full object-contain transform group-hover:scale-110 transition-transform duration-700 ease-out"
                  onError={(e) =>
                    onImgErrorToPlaceholder(
                      e,
                      100,
                      100,
                      (part.title || 'Part').substring(0, 2).toUpperCase()
                    )
                  }
                />
              </div>
              <span
                className={`relative z-10 w-full text-center font-bold sm:font-black text-[9px] sm:text-[13px] uppercase tracking-tight line-clamp-2 leading-tight transition-colors duration-300 ${
                  darkCatalog
                    ? "text-slate-200 group-hover:text-amber-400"
                    : "text-gray-900 group-hover:text-blue-600"
                }`}
              >
                {part.title}
              </span>

              <div className="absolute bottom-4 right-4 hidden sm:block opacity-0 group-hover:opacity-100 transition-all duration-500 translate-x-4 group-hover:translate-x-0">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-white shadow-lg ${
                    darkCatalog ? "bg-amber-500" : "bg-blue-600"
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default ReplacementParts;
